import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import '../styles/components.css'
import './ImagesWithVideos.css'
import { BASE_URL } from '../helper/constants';
import { useDispatch } from 'react-redux';
import { SET_VIEWER_IINITAL_INDEX, SET_VIEWER_IMAGES, SET_VIEWER_IS_INTERNAL } from '../store';
import ReactPlayer from 'react-player'
import PdfPreviewTile from './PdfPreviewTile';
import PdfViewerModal from './PdfViewerModal';
import { loadedImagesCache } from '../utils/imageLoadCache';
import { isNativeVideoAttachment } from '../utils/chatVideo';
import VideoPreviewThumb from './chatMedia/VideoPreviewThumb';
import useMediaReveal from '../hooks/useMediaReveal';

function resolveFeedSrc(item, isC) {
    if (!item) return '';
    if (item.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('data:'))) {
        return item.url;
    }
    if (item.href) return item.href.startsWith('http') ? item.href : BASE_URL + item.href;
    const fileUrl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
    if (!fileUrl) return item.url || '';
    if (fileUrl.startsWith('blob:') || fileUrl.startsWith('data:') || fileUrl.startsWith('http')) return fileUrl;
    if (item.local || isC) {
        return item.url || (BASE_URL + fileUrl);
    }
    return BASE_URL + fileUrl;
}

function CachedFeedImage({ item, isC, style, onClick, className }) {
    const blobUrl = item?.fallbackUrl || (item?.url && (item.url.startsWith('blob:') || item.url.startsWith('data:')) ? item.url : '');
    const serverUrl = item?.serverUrl || '';
    const initial = blobUrl || resolveFeedSrc(item, isC);
    const [src, setSrc] = useState(initial);
    const [failed, setFailed] = useState(false);
    const reveal = useMediaReveal(src);

    useEffect(() => {
        setSrc(blobUrl || resolveFeedSrc(item, isC));
        setFailed(false);
    }, [blobUrl, item, isC]);

    useEffect(() => {
        if (!serverUrl) return;
        if (loadedImagesCache.has(serverUrl)) {
            setSrc(serverUrl);
            return;
        }
        let cancelled = false;
        let delay = 800;
        let timeout;
        const probe = () => {
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                loadedImagesCache.add(serverUrl);
                setSrc(serverUrl);
                setFailed(false);
            };
            img.onerror = () => {
                if (cancelled) return;
                timeout = setTimeout(probe, delay);
                delay = Math.min(delay * 1.6, 8000);
            };
            img.src = serverUrl;
        };
        timeout = setTimeout(probe, blobUrl ? 400 : 0);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [serverUrl, blobUrl]);

    if (failed && !blobUrl) {
        return <div className={className ? `${className} images-with-videos__failed` : 'images-with-videos__failed'} style={style} />;
    }

    return (
        <img
            ref={reveal.ref}
            className={`feed-image-transition ${reveal.className}${className ? ` ${className}` : ''}`}
            style={style}
            src={src}
            alt=""
            onClick={onClick}
            onLoad={reveal.done}
            onError={() => {
                if (blobUrl && src !== blobUrl) {
                    setSrc(blobUrl);
                    return;
                }
                reveal.fail();
                setFailed(true);
            }}
        />
    );
}

export function isPdfAttachment(item) {
    if (!item) return false;
    if (item.isPdf) return true;
    if (item.type === 'application/pdf') return true;
    const candidates = [item.fileUrl, item.href, item.url, item.name, item.attachedFileName];
    for (const c of candidates) {
        if (typeof c === 'string' && /\.pdf(\?|$)/i.test(c)) return true;
    }
    return false;
}

function YouTubeThumb({ id }) {
    const src = `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
    const reveal = useMediaReveal(src);
    return (
        <img
            ref={reveal.ref}
            src={src}
            alt=""
            className={`images-with-videos__yt-thumb ${reveal.className}`}
            onLoad={reveal.done}
            onError={reveal.fail}
        />
    );
}

function isVideoTile(item) {
    return !!(item && (item.videoType || isNativeVideoAttachment(item)));
}

function resolvePdfUrl(item) {
    if (!item) return '';
    if (item.url && (item.url.startsWith('blob:') || item.url.startsWith('http'))) return item.url;
    if (item.href) {
        if (item.href.startsWith('http')) return item.href;
        return BASE_URL + item.href;
    }
    if (item.fileUrl) {
        const furl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
        if (furl && furl.startsWith('http')) return furl;
        if (furl) return BASE_URL + furl;
    }
    return '';
}

function getPdfName(item) {
    if (!item) return 'document.pdf';
    if (item.attachedFileName) return String(item.attachedFileName).split('?')[0];
    if (item.originalName) return String(item.originalName).split('?')[0];
    if (item.name && !/^[0-9a-f-]{30,}\.pdf$/i.test(item.name)) return String(item.name).split('?')[0];
    return 'document.pdf';
}

/**
 * variant: 'feed' (big tile + side tiles), 'chat' (mobile Pro photo grid: at least 240 wide,
 * 3px gaps, radius 12; 1 photo 4:3 · 2 squares · one 2:1 over two squares · 2×2 with "+N"
 * over the fourth) or 'auto' (default): 'chat' inside a chat bubble's .chat-image-gallery.
 */
function ImagesWithVideos({ data, isC, isInternal = false, onPress: externalOnPress, variant = 'auto' }) {
    const dispatch = useDispatch();
    const rootRef = useRef(null);
    const [inChat, setInChat] = useState(variant === 'chat');
    useLayoutEffect(() => {
        if (variant !== 'auto') {
            setInChat(variant === 'chat');
            return;
        }
        const node = rootRef.current;
        setInChat(!!(node && node.closest && node.closest('.chat-image-gallery')));
    }, [variant]);
    const [pdfViewer, setPdfViewer] = useState(null);

    const openPdfViewer = useCallback((item) => {
        const url = resolvePdfUrl(item);
        if (url) setPdfViewer({ url, name: getPdfName(item) });
    }, []);

    const closePdfViewer = useCallback(() => setPdfViewer(null), []);

    const onlyFour = useMemo(() => {
        if (data && data.length > 0) {
            if (data.length >= 4) {
                return data.slice(1, 4);
            }
            return data;
        }
        return [];
    }, [data]);

    const onPress = (s, index) => {
        const item = data[index];
        if (item && isPdfAttachment(item)) return;

        const nonPdfData = data.filter((d) => !isPdfAttachment(d));
        let adjustedIndex = 0;
        let count = 0;
        for (let i = 0; i < data.length; i++) {
            if (isPdfAttachment(data[i])) continue;
            if (i === index) { adjustedIndex = count; break; }
            count++;
        }

        if (externalOnPress) {
            externalOnPress(nonPdfData, adjustedIndex);
        } else {
            dispatch({ type: SET_VIEWER_IINITAL_INDEX, index: adjustedIndex });
            dispatch({ type: SET_VIEWER_IMAGES, ViewerImages: nonPdfData });
            dispatch({ type: SET_VIEWER_IS_INTERNAL, isInternal: isInternal });
        }
    }

    const _renderVideoWithPreview = (val) => {
        if (isNativeVideoAttachment(val)) {
            return <VideoPreviewThumb item={val} playSize={36} className="images-with-videos__video-thumb" />;
        }
        if (val && val.href) {
            const ytId = String(val.href).match(
                /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/
            )?.[1];
            if (ytId) {
                return <YouTubeThumb id={ytId} />;
            }
            return (
                <ReactPlayer
                    light={true}
                    url={val.href}
                    controls={false}
                    muted={true}
                    height={'100%'}
                    width={'100%'}
                    style={{ backgroundColor: "#fff" }}
                    playIcon={<></>}
                />
            );
        }
        return null;
    }

    const _renderVideoPlayerPlaceholder = (val, type, mediaIndex = 0) => {
        const native = isNativeVideoAttachment(val);
        const isSingle = type == 1;
        return (
            <div
                className={`${isSingle ? '' : 'feed-media-video '}images-with-videos__video ${isSingle ? 'images-with-videos__video--single' : 'images-with-videos__video--tile'}${native ? ' images-with-videos__video--native' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    onPress(data, mediaIndex);
                }}
            >
                {_renderVideoWithPreview(val, type)}
                {!native && (
                    <div className="images-with-videos__play-overlay">
                        <div className="kj-flex-1" />
                        <img className="images-with-videos__play-icon" src={process.env.PUBLIC_URL + '/assets/img/DN4X.png'} alt="" />
                        <div className="kj-flex-1" />
                    </div>
                )}
            </div>
        );
    };

    // Chat bubbles: the mobile Pro photo grid (ProMediaBlocks.ProPhotoGrid).
    if (inChat && data.length > 0) {
        const shown = data.slice(0, 4);
        const more = data.length > 4 ? data.length - 4 : 0;
        const cell = (val, index, size) => (
            <div
                key={index}
                className={`iwv-chat__cell iwv-chat__cell--${size}`}
                onClick={() => {
                    if (!isPdfAttachment(val)) onPress(data, index);
                }}
            >
                {isVideoTile(val) ? (
                    _renderVideoPlayerPlaceholder(val, undefined, index)
                ) : isPdfAttachment(val) ? (
                    <div className="iwv-chat__pdf">
                        <PdfPreviewTile item={val} onClick={() => openPdfViewer(val)} isInternal={isInternal} />
                    </div>
                ) : (
                    <CachedFeedImage item={val} isC={isC} className="images-with-videos__tile-img" />
                )}
                {index === 3 && more > 0 ? (
                    <div className="iwv-chat__more" aria-label={`+${more}`}>
                        <div className="iwv-chat__more-count">+{more}</div>
                    </div>
                ) : null}
            </div>
        );
        let grid;
        if (shown.length === 1) {
            grid = cell(shown[0], 0, 'single');
        } else if (shown.length === 2) {
            grid = <div className="iwv-chat__row">{shown.map((v, i) => cell(v, i, 'half'))}</div>;
        } else if (shown.length === 3) {
            grid = (
                <>
                    {cell(shown[0], 0, 'wide')}
                    <div className="iwv-chat__row">{shown.slice(1).map((v, i) => cell(v, i + 1, 'half'))}</div>
                </>
            );
        } else {
            grid = (
                <>
                    <div className="iwv-chat__row">{shown.slice(0, 2).map((v, i) => cell(v, i, 'half'))}</div>
                    <div className="iwv-chat__row">{shown.slice(2, 4).map((v, i) => cell(v, i + 2, 'half'))}</div>
                </>
            );
        }
        return (
            <div ref={rootRef} className="images-with-videos iwv-chat">
                {grid}
                {pdfViewer && (
                    <PdfViewerModal url={pdfViewer.url} fileName={pdfViewer.name} onClose={closePdfViewer} />
                )}
            </div>
        );
    }

    return (
        <div ref={rootRef} className="images-with-videos">
            {data.length > 0 &&
                (data.length > 3 ? (
                    <div className="feed-media-grid">
                    <div className="feed-media-row is-many">
                        <div className="feed-media-cell images-with-videos__main-cell">
                            {isVideoTile(data[0]) ? (
                                _renderVideoPlayerPlaceholder(data[0], undefined, 0)
                            ) : isPdfAttachment(data[0]) ? (
                                <div className="kj-flex-1">
                                    <PdfPreviewTile item={data[0]} onClick={() => openPdfViewer(data[0])} isInternal={isInternal} />
                                </div>
                            ) : (
                                <div
                                    onClick={() => {
                                        onPress(data, 0)
                                    }}
                                >
                                    <CachedFeedImage
                                        item={data[0]}
                                        isC={isC}
                                        className="feed-media-fill images-with-videos__tile-img"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="images-with-videos__side">
                            {onlyFour.map((val, index) => (
                                <div
                                    key={index}
                                    className={`feed-media-cell images-with-videos__side-cell${index === 0 ? '' : ' images-with-videos__side-cell--gap'}`}
                                    onClick={() => {
                                        if (!isPdfAttachment(val)) onPress(data, index+1)
                                    }}
                                >
                                    {isVideoTile(val) ? (
                                        _renderVideoPlayerPlaceholder(val, undefined, index + 1)
                                    ) : isPdfAttachment(val) ? (
                                        <PdfPreviewTile item={val} onClick={() => openPdfViewer(val)} isInternal={isInternal} />
                                    ) : (
                                        <CachedFeedImage
                                            item={val}
                                            isC={isC}
                                            className="feed-media-fill images-with-videos__tile-img"
                                        />
                                    )}
                                    {index === onlyFour.length - 1 && data.length - 4 > 0 && (
                                        <div className="images-with-videos__more">
                                            <span className="images-with-videos__more-count">
                                                +{data.length - 4}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    </div>
                ) : (
                    data.length == 3 ?
                        <div className="feed-media-grid">
                        <div className="feed-media-row is-three">
                            <div className="feed-media-cell images-with-videos__main-cell">
                                {isVideoTile(data[0]) ? (
                                    _renderVideoPlayerPlaceholder(data[0], 3, 0)
                                ) : isPdfAttachment(data[0]) ? (
                                    <div className="kj-flex-1">
                                        <PdfPreviewTile item={data[0]} onClick={() => openPdfViewer(data[0])} isInternal={isInternal} />
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => {
                                            onPress(data, 0)
                                        }}
                                    >
                                        <CachedFeedImage
                                            item={data[0]}
                                            isC={isC}
                                            className="feed-media-fill images-with-videos__tile-img"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="images-with-videos__side">
                                {data.map((val, index) => (
                                    index != 0 ?
                                        <div
                                            key={index}
                                            className={`feed-media-cell images-with-videos__side-cell images-with-videos__side-cell--half${index === 1 ? '' : ' images-with-videos__side-cell--gap'}`}
                                            onClick={() => {
                                                if (!isPdfAttachment(val)) onPress(data, index)
                                            }}
                                        >
                                            {isVideoTile(val) ? (
                                                _renderVideoPlayerPlaceholder(val, undefined, index)
                                            ) : isPdfAttachment(val) ? (
                                                <PdfPreviewTile item={val} onClick={() => openPdfViewer(val)} isInternal={isInternal} />
                                            ) : (
                                                <CachedFeedImage
                                                    item={val}
                                                    isC={isC}
                                                    className="feed-media-fill images-with-videos__tile-img"
                                                />
                                            )}
                                            {index === onlyFour.length - 1 && data.length - 4 > 0 && (
                                                <div className="images-with-videos__more-plain">
                                                    <span className="images-with-videos__more-count-plain">
                                                        +{data.length - 4}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        :
                                        <></>
                                ))}
                            </div>
                        </div>
                        </div>
                        :
                        data.length == 2 ?
                            <div className="feed-media-grid">
                            <div className="feed-media-row is-two">
                                {data.map((val, index) => (
                                    <div key={index} className="feed-media-tile">
                                        {isVideoTile(val) ? (
                                            _renderVideoPlayerPlaceholder(val, 2, index)
                                        ) : isPdfAttachment(val) ? (
                                            <div>
                                                <PdfPreviewTile item={val} onClick={() => openPdfViewer(val)} isInternal={isInternal} />
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => {
                                                    onPress(data, index)
                                                }}
                                            >
                                                <CachedFeedImage
                                                    item={val}
                                                    isC={isC}
                                                    className="feed-media-fill images-with-videos__tile-img"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            </div>
                            :
                            (() => {
                                const dim = data[0].imageDimension || data[0];
                                const w = dim?.width || 0;
                                const h = dim?.height || 0;
                                const hasRatio = w > 0 && h > 0;
                                return (
                                    <div onClick={() => { if (!isPdfAttachment(data[0])) onPress(data, 0); }} className="mw-100">
                                        {isVideoTile(data[0]) ? (
                                            _renderVideoPlayerPlaceholder(data[0], 1, 0)
                                        ) : isPdfAttachment(data[0]) ? (
                                            <div className="w-100 mw-100">
                                                <PdfPreviewTile item={data[0]} onClick={() => openPdfViewer(data[0])} isInternal={isInternal} />
                                            </div>
                                        ) : (
                                            <CachedFeedImage
                                                item={data[0]}
                                                isC={isC}
                                                className="images-with-videos__single-img"
                                                style={{ '--aspect': hasRatio ? `${w} / ${h}` : '4 / 3' }}
                                            />
                                        )}
                                    </div>
                                );
                            })()
                ))}

            {pdfViewer && (
                <PdfViewerModal
                    url={pdfViewer.url}
                    fileName={pdfViewer.name}
                    onClose={closePdfViewer}
                />
            )}
        </div>
    )
}



export default ImagesWithVideos